import { computed, type WritableSignal } from '@angular/core';
import { type SelectOption } from '@bizzdesign/shared/models';
import {
  type DisplayOption,
  displaySettings,
  gridOption,
  listOption,
  sortOptionMap,
  type SortOption,
} from '@bizzdesign/workspaces/utils';

/**
 * Shared grid/list display mode and sort-option controls for the Views and
 * Workspaces search-result tabs.
 *
 * @remarks
 * Subclasses supply the `displayOption` and `sortOption` signals with their own
 * defaults; everything else (the shared option lists, the derived selected sort
 * option, and the dropdown change handlers) lives here so the two tabs don't
 * duplicate it.
 */
export abstract class SearchResultsDisplayBase {
  readonly displaySettings = displaySettings;
  readonly gridOption = gridOption;
  readonly listOption = listOption;
  abstract readonly displayOption: WritableSignal<DisplayOption>;
  abstract readonly sortOption: WritableSignal<SortOption>;
  readonly selectedSortOption = computed(
    () => sortOptionMap[this.sortOption()],
  );

  /**
   * Applies the chosen display mode (grid/list), ignoring a cleared selection.
   *
   * @param selectedOption - The option emitted by the display-mode select.
   */
  onDisplayChange(
    selectedOption: SelectOption<DisplayOption> | undefined,
  ): void {
    if (selectedOption !== undefined) {
      this.displayOption.set(selectedOption.key);
    }
  }

  /**
   * Applies the chosen sort option, ignoring a cleared selection.
   *
   * @param selectedOption - The option emitted by the sort select.
   */
  onSortChange(selectedOption: SelectOption<SortOption> | undefined): void {
    if (selectedOption !== undefined) {
      this.sortOption.set(selectedOption.key);
    }
  }
}
